import { Link } from 'react-router-dom'
import Breadcrumb from '@/components/ui/Breadcrumb'
import Pagination from '@/components/ui/Pagination'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import RecentArticles from '@/components/sections/RecentArticles'
import Newsletter from '@/components/sections/Newsletter'
import {
  useFilterStrategicPillars,
  useSearchStrategicPillars
} from '@/lib/api/hooks/strategic-pillar'
import { useAllWebPages } from '@/lib/api/hooks/web-pages'
import FullPageSpinner from '@/components/ui/FullPageSpinner'
import { motion } from 'framer-motion'
import { fadeUp, staggerContainer, viewportReveal } from '@/lib/motion'
import { parseHtmlString } from '@/utils/helpers/html'

import Search from '@/components/ui/Search'

import FilterParamsDisplay from '@/components/ui/FilterParamsDisplay'

import { useSearchFilterParams } from '@/hooks/useSearchFilterParams'

import FILTER_SECTIONS from '@/components/pages/ProgrammesAndPriorities/StrategicPillars/FilterModule/FILTER_SECTIONS'

import FilterModule from '@/components/ui/FilterModule'

interface Program {
  id: string
  title: string
  description: string
  image: string
  category: string
  href: string
}

const parseHTMLContent = (html: string): string => {
  if (!html) return ''
  if (typeof document !== 'undefined') {
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = html
    return tempDiv.textContent || tempDiv.innerText || ''
  }
  return html.replace(/<[^>]*>/g, '').trim()
}



const StrategicPillars = () => {
  const { data, isFetching, isError, refetch } = useAllWebPages()
  const pillarsData = data?.data && data?.data.find((page) =>
    page.codeName?.toLowerCase() === 'strategic_pillars' ||
    page.websitePageName?.toLowerCase() === 'strategic pillars'
  )?.content || null

  // ── URL-driven search / filter state ──────────────────────────────────────
  const {
    mode,
    searchTerm,
    filterState,
    page,
    size,
    setSearch,
    setFilters,
    removeFilter,
    clearFilters,
    setPage,
    apiFilters,
    getActiveFilterCount
  } = useSearchFilterParams(FILTER_SECTIONS)


  const { data: searchData, isFetching: isSearchFetching , isError: isSearchError , refetch: refetchSearch } = useSearchStrategicPillars(searchTerm, {
    page,
    size
  })

  const { data: filterData, isFetching: isFilterFetching , isError: isFilterError , refetch: refetchFilter } = useFilterStrategicPillars(
    { page, size },
    apiFilters
  )

  const strategicPillarsData = mode === 'search' ? searchData : filterData
  const apiStrategicPillars = strategicPillarsData?.data?.data || []
  const totalPages = strategicPillarsData?.data?.totalPage || 1
  const isLoading = isSearchFetching || isFilterFetching
  const searchFilterError = isSearchError || isFilterError
  const refetchData = mode === 'search' ? refetchSearch : refetchFilter
      const activeFilterCount = getActiveFilterCount(FILTER_SECTIONS)
      const emptyMessage = mode === 'search' ? 'No strategic pillars found for this search query.' : mode === "filter" ? 'No strategic pillars found for this filter.' : 'No strategic pillars found.'


  const programs: Program[] = apiStrategicPillars.map((pillar) => {
    const textContent = parseHTMLContent(pillar.content || '')
    const description = textContent
      ? textContent.substring(0, 200) + (textContent.length > 200 ? '...' : '')
      : ''

    return {
      id: pillar.strategicPillarId,
      title: pillar.title,
      description,
      image: pillar.coverImage,
      category: 'Strategic Pillars',
      href: `/programmes/strategic-pillars/${pillar.strategicPillarId}`
    }
  })


  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Strategic Pillars', href: '/programmes', active: true }
  ]


  if (isFetching) {
    return <FullPageSpinner />
  }

  if (isError) {
    return <div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4">
      <p>Error loading page.</p>
      <button onClick={() => refetch()} className="px-4 py-2 bg-wine-100 hover:bg-wine-200 text-white rounded">Retry</button>
    </div>
  }

  if (!pillarsData) {
    return <div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4">
      <p>Page content not found.</p>
    </div>
  }

  return (
    <motion.div
      className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8"
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
    >
      <motion.div className="mb-5" variants={fadeUp}>
        <Breadcrumb items={breadcrumbItems} />
      </motion.div>

      <motion.div className="mb-16" variants={fadeUp}>
        <img
          src={pillarsData?.STRATEGIC_PILLAR_BANNER?.fileUrl ?? "https://res.cloudinary.com/dlwegkhme/image/upload/v1756135981/b467cbb129e3f72467993c785f41b2ffab8d0f62_1_cqe6qb.png"}
          alt="African Space Agency Building with Strategic Pillars"
          className="w-full h-64 sm:h-80 md:h-96 lg:h-[480px] object-cover "
        />
      </motion.div>

      <motion.div className="mb-16 space-y-6" variants={fadeUp}>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-normal font-pt-serif text-gray-900 leading-tight lg:leading-12">
          {pillarsData?.STRATEGIC_PILLARS?.title ?? "Harnessing Space Technology for Africa's Sustainable Development"}
        </h1>
        <div className="max-w-4xl" dangerouslySetInnerHTML={parseHtmlString(pillarsData?.STRATEGIC_PILLARS?.description ?? "")}>
        </div>
      </motion.div>

      <motion.div
        className="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-16"
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={viewportReveal}
      >
        <div className="lg:col-span-1 order-2 lg:order-1">
          <div className="space-y-10">
            <RecentArticles />
            <Newsletter />
          </div>
        </div>

        <div className="lg:col-span-3 order-1 lg:order-2">
          <div className="space-y-8">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-normal font-pt-serif text-gray-900">
              STRATEGIC PILLARS
            </h2>

               {/* Search + Filter Controls */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center relative gap-4 justify-between flex-wrap">
              <Search
                value={searchTerm}
                onSearch={setSearch}
                placeholder="Search strategic pillars..."
              />
             <div style={{display: "none"}}>
                <FilterModule
                    sections={FILTER_SECTIONS}
                    activeFilters={filterState}
                    onApply={setFilters}
                    activeFilterCount={activeFilterCount}
                  />
              </div>
            </div>

            {/* Active filter chips — only shown in filter mode */}
            <FilterParamsDisplay
              sections={FILTER_SECTIONS}
              filterState={filterState}
              onRemove={removeFilter}
              onClearAll={clearFilters}
            />
          </div>

            <div className="space-y-6">
              {
                isLoading ? (
                  <div className="py-20">
                    <LoadingSpinner size="lg" message="Loading strategic pillars..." />
                  </div>
                ) : searchFilterError ? (
                  <div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4">
                    <p>Error loading strategic pillars.</p>
                    <button onClick={() => refetchData()} className="px-4 py-2 bg-wine-100 hover:bg-wine-200 text-white rounded">Retry</button>
                  </div>
                ) : programs.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-lg">{emptyMessage}</p>
                  </div>
                ) : 
              <motion.div
                className="grid grid-cols-1 lg:grid-cols-2 gap-6"
                variants={staggerContainer}
              >
                {programs.map((program) => (
                  <motion.div key={program.id} variants={fadeUp}>
                    <Link
                      to={program.href}
                      className="group block"
                    >
                      <article className="space-y-4">
                        <div className="w-full h-72">
                          <img
                            src={program.image}
                            alt={program.title}
                            className="w-full h-full object-cover rounded group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <div className="space-y-4">
                          <h3 className="text-xl sm:text-2xl font-normal font-pt-serif text-gray-900 leading-7 group-hover:text-wine-100 transition-colors">
                            {program.title}
                          </h3>
                          <p className="text-base font-normal font-satoshi text-gray-700 leading-relaxed line-clamp-3">
                            <span dangerouslySetInnerHTML={parseHtmlString(program.description)} />
                          </p>
                        </div>
                      </article>
                    </Link>
                  </motion.div>
                ))}
              </motion.div>
}
              

                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
            
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  )
}

export default StrategicPillars
